import { useCallback, useMemo } from 'react'
import { useDataValue } from 'Simple/Data.js'
import { dateShortWithMonthAndTime } from 'Data/format.js'
import { getPayorName, isEntityType, LEDGER_ENTRY_TYPE } from 'Data/payments.js'

import View from './view.js'
import ActionsCell from './ActionsCell/index.js'
import TypeCell from './TypeCell/index.js'
import NotesCellTooltip from './NotesCellTooltip/index.js'
import Expanded from './Expanded/index.js'

let tooltipValueGetter = params => ({ value: params.value })

export default function Logic(props) {
  let transactions = useDataValue({
    context: 'transactions',
    viewPath: props.viewPath,
  })
  let filters = useDataValue({
    context: 'tab',
    path: 'selected.ledger.notes.filter_by',
    viewPath: props.viewPath,
  })

  let columnDefs = useMemo(
    () => [
      {
        field: 'sys_created',
        headerName: 'Date / Time',
        initialWidth: 180,
        minWidth: 160,
        valueFormatter: params => dateShortWithMonthAndTime(params.value),
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: 'type',
        headerName: 'Type',
        initialWidth: 140,
        minWidth: 120,
        cellRenderer: params => (
          <TypeCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/TypeCell(${params.data.id})`}
          />
        ),
      },
      {
        field: 'payment_account',
        headerName: 'Payor',
        initialWidth: 180,
        minWidth: 120,
        valueGetter: params => getPayorName(params.data.payment_account),
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'notes',
        headerName: 'Notes',
        initialWidth: 300,
        minWidth: 200,
        tooltipValueGetter,
        tooltipComponent: params => (
          <NotesCellTooltip
            {...params}
            viewPath={`${props.viewPath}/NotesCellTooltip(${params.data.id})`}
          />
        ),
        valueGetter: params => params.data.notes || '-',
        cellStyle: {
          color: '#52606D',
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        initialWidth: 80,
        minWidth: 70,
        cellRenderer: params => (
          <ActionsCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/ActionsCell(${params.data.id})`}
          />
        ),
      },
    ],
    [props.viewPath]
  )

  let getRowStyle = useCallback(params => {
    if (params.data.type === LEDGER_ENTRY_TYPE.PAYMENT) {
      return { backgroundColor: '#F8F9FA' }
    }
  }, [])

  let onRowClicked = useCallback(params => {
    let node = params.node

    let target = params.event.target

    // Don't expand/collapse if clicking on a button or link (e.g. clicking on more actions)
    if (target.closest('button') || target.closest('a')) {
      return
    }

    node.setExpanded(!node.expanded)
  }, [])

  let filteredData = useMemo(() => {
    // Empty array means "all" - no filtering
    if (filters.length === 0) {
      return transactions
    }

    return transactions.filter(item => filters.some(f => isEntityType(item, f)))
  }, [filters, transactions])

  return (
    <View
      {...props}
      columnDefs={columnDefs}
      rowData={filteredData}
      height="100%"
      masterDetail={true}
      detailCellRenderer={params => (
        <Expanded
          {...params}
          key={params.data.id}
          viewPath={`${props.viewPath}/Expanded(${params.data.id})`}
        />
      )}
      detailRowAutoHeight={true}
      onRowClicked={onRowClicked}
      getRowStyle={getRowStyle}
    />
  )
}
